import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createAmbulance } from '../../api/encounters.js'
import { listAmbulanceItems } from '../../api/ambulance.js'
import OrgSelect from '../../components/OrgSelect.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import { statusColor, formatDateTime } from '../../utils/format.js'
import {
  URGENCY_AMBULANCE,
  URGENCY_AMBULANCE_DEFAULT,
  AMBULANCE_SERVICE_TYPES,
  AMBULANCE_SERVICE_TYPE_DEFAULT,
} from '../../constants/index.js'
import usePersistedState from '../../hooks/usePersistedState.js'

export default function AmbulanceForm({ encounter, locked }) {
  const qc = useQueryClient()
  // Transient form state persisted to localStorage so half-typed
  // ambulance requests survive HMR re-mounts, accidental tab closes,
  // and "I'll come back to it in 5 minutes" interruptions. Cleared
  // automatically on successful submit (state reset → empty → key
  // removed by usePersistedState).
  const draftKey = `amb:enc${encounter.id}`
  const [providerId, setProviderId] = usePersistedState(`${draftKey}:provider`, null)
  const [catalogueItem, setCatalogueItem] = usePersistedState(`${draftKey}:catalogue_item`, null)
  const [pickup, setPickup] = usePersistedState(`${draftKey}:pickup`, '')
  const [destination, setDestination] = usePersistedState(`${draftKey}:destination`, '')
  const [urgency, setUrgency] = usePersistedState(`${draftKey}:urgency`, URGENCY_AMBULANCE_DEFAULT)
  const [serviceType, setServiceType] = usePersistedState(
    `${draftKey}:service_type`, AMBULANCE_SERVICE_TYPE_DEFAULT)
  const [summary, setSummary] = usePersistedState(`${draftKey}:summary`, '')
  const [formError, setFormError] = useState(null)

  // Packages offered by the chosen provider — picking one snapshots its
  // price onto the dispatch and drives ambulance value delivered.
  const itemsQ = useQuery({
    queryKey: ['amb-items', providerId],
    queryFn: () => listAmbulanceItems({ service: providerId, is_offered: true }),
    enabled: !!providerId,
  })

  const mut = useMutation({
    mutationFn: (body) => createAmbulance(encounter.id, body),
    onSuccess: (newReq) => {
      // Splice the new request into the encounter cache directly from
      // the POST response so the row appears instantly without waiting
      // for an invalidate + refetch round-trip.
      qc.setQueryData(['encounter', String(encounter.id)], (prev) => {
        if (!prev || !newReq) return prev
        return {
          ...prev,
          ambulance_requests: [...(prev.ambulance_requests || []), newReq],
        }
      })
      qc.invalidateQueries({ queryKey: ['encounter', String(encounter.id)] })
      setPickup(''); setDestination(''); setSummary('')
      setCatalogueItem(null)
      setUrgency(URGENCY_AMBULANCE_DEFAULT)
      setServiceType(AMBULANCE_SERVICE_TYPE_DEFAULT)
    },
  })

  function onSubmit(e) {
    e.preventDefault()
    setFormError(null)
    if (!providerId) return setFormError('Select an ambulance provider.')
    if (!pickup.trim() || !destination.trim()) return setFormError('Pickup and destination are required.')
    mut.mutate({
      target_provider_id: providerId,
      // service_type and urgency are deliberately distinct: type
      // says WHAT kind of transport, urgency says HOW FAST. Sending
      // both keeps the dispatch queue readable (a "Dialysis run"
      // can be routine; an "ICU transfer" can be priority; etc.).
      service_type: serviceType,
      // Backend field is `pickup_location`; the FE state var is named
      // `pickup` for brevity. Sending the wrong key (pickup_address)
      // silently dropped the value into the void — that was the
      // "Pickup not showing on existing requests" bug.
      pickup_location: pickup,
      destination,
      urgency,
      clinical_summary: summary,
      catalogue_item: catalogueItem || undefined,
    })
  }

  const existing = encounter.ambulance_requests || []

  return (
    <Card>
      <CardHeader title="Ambulance requests" subtitle="Dispatch a transfer" />

      {existing.length > 0 && (
        <div className="space-y-3 mb-4">
          {existing.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${statusColor(a.status)}`}>{a.status}</span>
                <span className="font-mono text-slate-500">#{a.id}</span>
                {a.provider_name && (
                  <span className="text-slate-700"><span className="text-slate-400">→</span> {a.provider_name}</span>
                )}
                {a.urgency && <span className="text-[10px] uppercase border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">{a.urgency}</span>}
                {a.created_at && <span className="text-slate-400">· Requested {formatDateTime(a.created_at)}</span>}
              </div>
              <div className="mt-2 text-sm text-slate-800">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {a.pickup_location && <div><span className="text-[10px] uppercase tracking-wide text-slate-400">Pickup </span>{a.pickup_location}</div>}
                  {a.destination && <div><span className="text-[10px] uppercase tracking-wide text-slate-400">Destination </span>{a.destination}</div>}
                </div>
              </div>
              {a.clinical_summary && (
                <div className="mt-2 text-[11px] text-slate-500">
                  <span className="uppercase tracking-wide text-slate-400">Reason </span>
                  {a.clinical_summary}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {existing.length === 0
            ? 'No ambulance requests were recorded on this encounter.'
            : 'Encounter is locked. Add new requests via an amended encounter.'}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Ambulance provider</span>
            <div className="mt-1">
              <OrgSelect orgType="ambulance" value={providerId} onChange={setProviderId} placeholder="Select provider…" />
            </div>
          </label>

          {providerId && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Package (sets the price)</span>
              <select
                value={catalogueItem || ''}
                onChange={(e) => setCatalogueItem(e.target.value ? parseInt(e.target.value, 10) : null)}
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">{itemsQ.isLoading ? 'Loading packages…' : 'No package / set price later'}</option>
                {(itemsQ.data || []).map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}{it.base_price_pgk != null ? ` — K ${Number(it.base_price_pgk).toFixed(2)}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Pickup address</span>
              <input value={pickup} onChange={(e) => setPickup(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Destination</span>
              <input value={destination} onChange={(e) => setDestination(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Service type</span>
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
              >
                {AMBULANCE_SERVICE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Urgency</span>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
              >
                {URGENCY_AMBULANCE.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Clinical summary</span>
            <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
          </label>

          {(formError || mut.error) && <ErrorBanner error={formError || mut.error} />}

          <div className="flex justify-end">
            <button disabled={mut.isPending} className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60">
              {mut.isPending ? 'Saving…' : 'Add ambulance request'}
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}
